import { useRef, useState } from 'react';
import Avatar from '../../components/Avatar/Avatar';
import { useLinkedAccounts } from '../../context/LinkedAccountsContext';
import styles from './AvatarUploadControl.module.scss';

import avatar1 from '../../assets/avatars/avatar_1.png';
import avatar2 from '../../assets/avatars/avatar_2.png';
import avatar3 from '../../assets/avatars/avatar_3.png';
import avatar4 from '../../assets/avatars/avatar_4.png';
import avatar5 from '../../assets/avatars/avatar_5.png';
import avatar6 from '../../assets/avatars/avatar_6.png';
import avatar7 from '../../assets/avatars/avatar_7.png';
import avatar8 from '../../assets/avatars/avatar_8.png';
import avatar9 from '../../assets/avatars/avatar_9.png';
import avatar10 from '../../assets/avatars/avatar_10.png';
import avatar11 from '../../assets/avatars/avatar_11.png';
import avatar12 from '../../assets/avatars/avatar_12.png';
import avatar13 from '../../assets/avatars/avatar_13.png';
import avatar14 from '../../assets/avatars/avatar_14.png';
import avatar15 from '../../assets/avatars/avatar_15.png';
import avatar16 from '../../assets/avatars/avatar_16.png';
import avatar17 from '../../assets/avatars/avatar_17.png';
import avatar18 from '../../assets/avatars/avatar_18.png';
import avatar19 from '../../assets/avatars/avatar_19.png';
import avatar20 from '../../assets/avatars/avatar_20.png';

const presetAvatars = [
  avatar1, avatar2, avatar3, avatar4, avatar5,
  avatar6, avatar7, avatar8, avatar9, avatar10,
  avatar11, avatar12, avatar13, avatar14, avatar15,
  avatar16, avatar17, avatar18, avatar19, avatar20,
];

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

interface AvatarUploadControlProps {
  avatarPreview: string | null;
  username: string;
  onSelect: (file: File) => void;
  onPresetSelect?: (avatarId: string, previewUrl: string) => void;
  error?: string;
  hasAvatar: boolean;
}

export default function AvatarUploadControl({
  avatarPreview,
  username,
  onSelect,
  onPresetSelect,
  error,
  hasAvatar,
}: AvatarUploadControlProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { accounts } = useLinkedAccounts();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);

  const [selectedPresetIndex, setSelectedPresetIndex] = useState<number | null>(null);
  const [selectedLinkedUrl, setSelectedLinkedUrl] = useState<string | null>(null);

  // Build linked account avatar options
  const linkedAvatars: { provider: string; avatarUrl: string }[] = [];
  if (accounts) {
    for (const [provider, data] of Object.entries(accounts)) {
      if (data?.avatar) {
        linkedAvatars.push({ provider, avatarUrl: data.avatar });
      }
    }
  }

  const handleOverlayClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    onSelect(file);
    setShowPicker(false);
  };

  const handlePresetClick = (index: number) => {
    setSelectedPresetIndex(index);
    setSelectedLinkedUrl(null);
    setPickerError(null);
  };

  const handleLinkedClick = (avatarUrl: string) => {
    setSelectedLinkedUrl(avatarUrl);
    setSelectedPresetIndex(null);
    setPickerError(null);
  };

  const handleConfirm = () => {
    if (selectedPresetIndex !== null) {
      const avatarId = `avatar_${selectedPresetIndex + 1}`;
      const previewUrl = presetAvatars[selectedPresetIndex];
      onPresetSelect?.(avatarId, previewUrl);
    } else if (selectedLinkedUrl) {
      // For linked account, use "linked" as a signal and the URL as preview
      onPresetSelect?.('__linked__', selectedLinkedUrl);
    }

    setShowPicker(false);
    setSelectedPresetIndex(null);
    setSelectedLinkedUrl(null);
    setPickerError(null);
  };

  const hasSelection = selectedPresetIndex !== null || selectedLinkedUrl !== null;

  return (
    <div className={styles.container}>
      <div className={styles.avatarWrapper}>
        <Avatar
          src={avatarPreview || undefined}
          username={username}
          size={96}
          className={styles.avatar}
        />
        <button
          type="button"
          className={styles.overlay}
          onClick={handleOverlayClick}
          onKeyDown={handleKeyDown}
          aria-label="Upload avatar"
        >
          <span className={styles.overlayIcon}>📷</span>
          <span className={styles.overlayText}>Upload</span>
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={handleFileChange}
        className={styles.fileInput}
        aria-label="Upload avatar image"
        tabIndex={-1}
      />

      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.presetBtn}
          onClick={() => setShowPicker(!showPicker)}
        >
          {showPicker ? 'Hide avatars' : 'Choose avatar'}
        </button>
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {/* Preset avatar picker */}
      {showPicker && (
        <div className={styles.pickerPanel}>
          {pickerError && <p className={styles.pickerError}>{pickerError}</p>}

          {linkedAvatars.length > 0 && (
            <div className={styles.linkedSection}>
              <p className={styles.pickerLabel}>Linked accounts</p>
              <div className={styles.linkedGrid}>
                {linkedAvatars.map((linked) => (
                  <button
                    key={linked.provider}
                    type="button"
                    className={`${styles.linkedItem} ${selectedLinkedUrl === linked.avatarUrl ? styles.selectedItem : ''}`}
                    onClick={() => handleLinkedClick(linked.avatarUrl)}
                    aria-label={`Use ${linked.provider} avatar`}
                  >
                    <img src={linked.avatarUrl} alt={linked.provider} className={styles.linkedImg} />
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className={styles.pickerLabel}>Default avatars</p>
          <div className={styles.presetGrid}>
            {presetAvatars.map((src, index) => (
              <button
                key={src}
                type="button"
                className={`${styles.presetItem} ${selectedPresetIndex === index ? styles.selectedItem : ''}`}
                onClick={() => handlePresetClick(index)}
                aria-label={`Select avatar ${index + 1}`}
              >
                <img src={src} alt={`Avatar ${index + 1}`} className={styles.presetImg} />
              </button>
            ))}
          </div>

          <div className={styles.pickerActions}>
            <button
              type="button"
              className={styles.confirmBtn}
              disabled={!hasSelection}
              onClick={handleConfirm}
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
